import { useState } from "react";
import {
  SplitContentCampaignConfig,
  SplitContentChannelConfig,
  SplitContentConfig,
  SplitContentContentGroupConfig,
  SplitContentDynamicTemplateConfig,
  SplitContentHtmlConfig,
  SplitContentNode,
  SplitContentNodeContent,
  SplitContentUrlConfig,
} from "./splitContentTypes";
import { handleSplit, handleDelete, onDragging, findNodeAndUpdateConfig } from "./splitContentUtils";
import { SplitContentMenu } from "./SplitContentMenu";
import { SplitContentAttachContentDialog } from "./attachConfig/SplitContentAttachContentDialog";
import { getCheckeredBg } from "@/lib/tailwindUtils";
import { SplitContentAttachHtmlDialog } from "./attachConfig/SplitContentAttachHtmlDialog";
import { SplitContentAttachDynamicTemplateDialog } from "./attachConfig/SplitContentAttachDynamicTemplateDialog";
import { parseViewportSize } from "@/components/canvas/utils/viewportUtils";
import { SplitContentLayout } from "./SplitContentLayout";
import { cn } from "@/lib/utils";
import { SplitContentAttachContentGroupDialog } from "./attachConfig/SplitContentAttachContentGroupDialog";
import { SplitContentAttachURLDialog } from "./attachConfig/SplitContentAttachURLDialog";
import { SplitContentAttachChannelDialog } from "./attachConfig/SplitContentAttachChannelDialog";
import { SplitContentAttachCampaignDialog } from "./attachConfig/SplitContentAttachCampaignDialog";

interface Props {
  viewportSize: string;
  layoutConfig: SplitContentNode;
  containerWidth: number;
  onLayoutChange: (layout: SplitContentNode) => void;
  defaultScreenSizeGuid: string;
}

export const SplitContentEditor: React.FC<Props> = ({
  viewportSize,
  containerWidth,
  layoutConfig,
  onLayoutChange,
  defaultScreenSizeGuid,
}) => {
  const [layout, setLayout] = useState<SplitContentNode>(layoutConfig);

  const [attachConfigType, setAttachConfigType] = useState<SplitContentConfig["type"]>();
  const [busyNode, setBusyNode] = useState<SplitContentNodeContent>();
  const [showManualSplitDialog, setShowManualSplitDialog] = useState(false);
  const [manualSplitNode, setManualSplitNode] = useState<SplitContentNodeContent>();
  const { width: layoutWidth, height: layoutHeight } = parseViewportSize(viewportSize);

  const scaleFactor = containerWidth / layoutWidth;

  const scaleViewportSize = (containerWidth: number): React.CSSProperties => {
    if (layoutWidth < containerWidth) {
      return {
        width: `${layoutWidth}px`,
        height: `${layoutHeight}px`,
      };
    }

    return {
      width: `${layoutWidth}px`,
      height: `${layoutHeight}px`,
      transform: `scale(${scaleFactor})`,
      transformOrigin: "top left",
      position: "absolute",
      left: 0,
      top: 0,
    };
  };

  const scaled = scaleViewportSize(containerWidth);

  return (
    <>
      <div className={cn(getCheckeredBg())} style={scaled}>
        <SplitContentLayout
          layout={layout}
          layoutWidth={layoutWidth}
          layoutHeight={layoutHeight}
          mode="edit"
          onDragging={(nodeId, [prevSize, nextSize]) =>
            setLayout((prevLayout) => {
              const newLayout = onDragging(prevLayout, nodeId, [prevSize || 50, nextSize || 50]);
              onLayoutChange(newLayout);
              return newLayout;
            })
          }
          renderExtraContentPerNode={(node, depth) => (
            <SplitContentMenu
              depth={depth}
              scaleFactor={scaleFactor}
              mode={node.config ? "edit" : "create"}
              onClickAttachContent={() => {
                setAttachConfigType("content");
                setBusyNode(node);
              }}
              onClickAttachContentGroup={() => {
                setAttachConfigType("content-group");
                setBusyNode(node);
              }}
              onClickAttachChannel={() => {
                setAttachConfigType("channel");
                setBusyNode(node);
              }}
              onClickAttachCampaign={() => {
                setAttachConfigType("campaign");
                setBusyNode(node);
              }}
              onClickAttachUrl={() => {
                setAttachConfigType("url");
                setBusyNode(node);
              }}
              onClickAttachHtml={() => {
                setAttachConfigType("html");
                setBusyNode(node);
              }}
              onClickAttachDynamicTemplate={() => {
                setAttachConfigType("dynamic-template");
                setBusyNode(node);
              }}
              onClickSplitManually={() => {
                setManualSplitNode(node);
                setShowManualSplitDialog(true);
              }}
              // when user clicks on split vertically, we should split the node horizontally on code
              onClickSplitVertically={() =>
                setLayout((prevLayout) => {
                  const newLayout = handleSplit(prevLayout, node, "horizontal");
                  onLayoutChange(newLayout);
                  return newLayout;
                })
              }
              // when user clicks on split horizontally, we should split the node vertically on code
              onClickSplitHorizontally={() =>
                setLayout((prevLayout) => {
                  const newLayout = handleSplit(prevLayout, node, "vertical");
                  onLayoutChange(newLayout);
                  return newLayout;
                })
              }
              onClickDelete={() =>
                setLayout((prevLayout) => {
                  const newLayout = handleDelete(prevLayout, node.id);
                  onLayoutChange(newLayout);
                  return newLayout;
                })
              }
              // When there is config attached to the node, we should disable the split actions
              // Not sure if this is a good idea, but it's a way to prevent users from splitting a node that has content attached to it
              // disableSplit={!!node.config}
            />
          )}
          manualSplitConfig={{
            showManualSplitDialog,
            setLayout,
            onLayoutChange,
            manualSplitNode: manualSplitNode!,
            onCloseManualSplitDialog: () => setShowManualSplitDialog(false),
          }}
        />
      </div>
      {busyNode && attachConfigType === "content" && (
        <SplitContentAttachContentDialog
          onClose={() => setAttachConfigType(undefined)}
          onAttached={(newConfig) => {
            setLayout((prevLayout) => {
              const newLayout = findNodeAndUpdateConfig(prevLayout, busyNode.id, newConfig);
              onLayoutChange(newLayout);
              return newLayout;
            });
          }}
        />
      )}
      {busyNode && attachConfigType === "content-group" && (
        <SplitContentAttachContentGroupDialog
          currentConfig={busyNode.config as SplitContentContentGroupConfig}
          onClose={() => setAttachConfigType(undefined)}
          defaultScreenSizeGuid={defaultScreenSizeGuid}
          onAttached={(newConfig) => {
            setLayout((prevLayout) => {
              const newLayout = findNodeAndUpdateConfig(prevLayout, busyNode.id, newConfig);
              onLayoutChange(newLayout);
              return newLayout;
            });
          }}
        />
      )}
      {busyNode && attachConfigType === "channel" && (
        <SplitContentAttachChannelDialog
          currentConfig={busyNode.config as SplitContentChannelConfig}
          onClose={() => setAttachConfigType(undefined)}
          defaultScreenSizeGuid={defaultScreenSizeGuid}
          onAttached={(newConfig) => {
            setLayout((prevLayout) => {
              const newLayout = findNodeAndUpdateConfig(prevLayout, busyNode.id, newConfig);
              onLayoutChange(newLayout);
              return newLayout;
            });
          }}
        />
      )}
      {busyNode && attachConfigType === "campaign" && (
        <SplitContentAttachCampaignDialog
          currentConfig={busyNode.config as SplitContentCampaignConfig}
          onClose={() => setAttachConfigType(undefined)}
          onAttached={(newConfig) => {
            setLayout((prevLayout) => {
              const newLayout = findNodeAndUpdateConfig(prevLayout, busyNode.id, newConfig);
              onLayoutChange(newLayout);
              return newLayout;
            });
          }}
        />
      )}
      {busyNode && attachConfigType === "html" && (
        <SplitContentAttachHtmlDialog
          currentConfig={busyNode.config as SplitContentHtmlConfig}
          onClose={() => setAttachConfigType(undefined)}
          onAttached={(newConfig) => {
            setLayout((prevLayout) => {
              const newLayout = findNodeAndUpdateConfig(prevLayout, busyNode.id, newConfig);
              onLayoutChange(newLayout);
              return newLayout;
            });
          }}
        />
      )}
      {busyNode && attachConfigType === "url" && (
        <SplitContentAttachURLDialog
          currentConfig={busyNode.config as SplitContentUrlConfig}
          onClose={() => setAttachConfigType(undefined)}
          onAttached={(newConfig) => {
            setLayout((prevLayout) => {
              const newLayout = findNodeAndUpdateConfig(prevLayout, busyNode.id, newConfig);
              onLayoutChange(newLayout);
              return newLayout;
            });
          }}
        />
      )}
      {busyNode && attachConfigType === "dynamic-template" && (
        <SplitContentAttachDynamicTemplateDialog
          currentConfig={busyNode.config as SplitContentDynamicTemplateConfig}
          onClose={() => setAttachConfigType(undefined)}
          onAttached={(newConfig) => {
            setLayout((prevLayout) => {
              const newLayout = findNodeAndUpdateConfig(prevLayout, busyNode.id, newConfig);
              onLayoutChange(newLayout);
              return newLayout;
            });
          }}
        />
      )}
    </>
  );
};
